import { type SelectionsReader } from '@databases/selections-api-types';
import { getService } from '@databases/service-container';
import { useCallback, useSyncExternalStore } from 'react';

import { databasesPingRegistry } from './ping-registry';

/**
 * Subscribes a React component to the database-level broadcast ping, which
 * fires on **every** selection change regardless of mode or entity.
 *
 * Use this for components that need aggregate selection state — selection
 * count, current mode, hasSelection, etc. — rather than entity-specific state.
 *
 * Follows the same "ping + read" pattern as `useSelectionPing`: on each ping,
 * re-evaluates the `selector` against `SelectionsReader` and triggers a React
 * state update only if the value changed.
 *
 * @example
 * ```ts
 * const selectionMode = useDatabaseSelectionPing(
 *   (reader) => reader.getSelectionMode(),
 * );
 *
 * const hasSelection = useDatabaseSelectionPing(
 *   (reader) => reader.hasSelection(),
 * );
 * ```
 *
 * @see createSelectionsPingEffect — the effect that fires the broadcast ping
 */
export function useDatabaseSelectionPing<T>(
  selector: (reader: SelectionsReader) => T
): T {
  /**
   * The Subscriber: Stable reference, only subscribes ONCE per mount.
   * We don't put `selector` in the dependencies, preventing thrashing.
   */
  const subscribe = useCallback((onStoreChange: () => void) => {
    const { key, subscribeTo } = databasesPingRegistry;
    const pingKey = key('database', {});

    return subscribeTo(pingKey, 'selectionChanged', onStoreChange);
  }, []);

  /**
   * The Snapshot Evaluator: Runs whenever the ping fires OR the component renders.
   * If `selector` is inline, this gets recreated. That's fine! useSyncExternalStore
   * compares the *result* (e.g. `false === false`).
   */
  const getSnapshot = useCallback(() => {
    const reader = getService('SelectionsReader');

    return selector(reader);
  }, [selector]);

  // The Bouncer: React's built-in performance guard for external stores.
  return useSyncExternalStore(subscribe, getSnapshot);
}
